import React from "react";
import { motion } from "framer-motion";
import { MapPin, Zap, Circle } from "lucide-react";

const BOUNDS = { latMin: 45.452, latMax: 45.495, lngMin: 9.115, lngMax: 9.2 };
const toXY = (lat, lng) => ({
  x: 8 + ((lng - BOUNDS.lngMin) / (BOUNDS.lngMax - BOUNDS.lngMin)) * 84,
  y: 88 - ((lat - BOUNDS.latMin) / (BOUNDS.latMax - BOUNDS.latMin)) * 76,
});

const STATUS_STYLES = {
  VACANT: { ring: "border-[#FFCC00]", dot: "#FFCC00", label: "VACANT", text: "text-[#FFCC00]" },
  LOCK: { ring: "border-[#007AFF]", dot: "#007AFF", label: "LOCK", text: "text-[#007AFF]" },
  BOOKED: { ring: "border-[#FF3B30]", dot: "#FF3B30", label: "BOOKED", text: "text-[#FF3B30]" },
  IDLE: { ring: "border-white/15", dot: "#71717A", label: "AVAILABLE", text: "text-zinc-500" },
};

const CATEGORY_LABELS = { padel: "PADEL", calcio: "FOOTBALL", tennis: "TENNIS", wellness: "WELLNESS" };
const catLabel = (c) => CATEGORY_LABELS[c] || c.toUpperCase();

const statusOf = (p, activeSlot, bookedProviderId) => {
  if (bookedProviderId === p.provider_id) return "BOOKED";
  if (activeSlot && activeSlot.provider_id === p.provider_id) return activeSlot.status;
  return "IDLE";
};

const PanelHeader = () => (
  <div className="flex items-center justify-between mb-4">
    <div>
      <p className="font-mono text-[11px] uppercase tracking-widest text-[#FF3B30]">
        // Partner Panel
      </p>
      <h2 className="font-head text-2xl sm:text-3xl font-bold uppercase tracking-tight text-white">
        Admin Simulator
      </h2>
    </div>
    <span className="flex items-center gap-1.5 rounded-full border border-white/10 bg-white/5 px-3 py-1 font-mono text-[11px] text-zinc-400">
      <Circle size={7} className="fill-[#25D366] text-[#25D366]" /> LIVE · Milano
    </span>
  </div>
);

const MAP_BG = {
  height: 300,
  backgroundImage:
    "linear-gradient(rgba(255,255,255,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.04) 1px, transparent 1px)",
  backgroundSize: "28px 28px",
};

const MapPinMarker = ({ provider, st, isSel, onSelect }) => {
  const { x, y } = toXY(provider.lat, provider.lng);
  const s = STATUS_STYLES[st];
  return (
    <button
      onClick={() => onSelect(provider.provider_id)}
      data-testid={`map-pin-${provider.provider_id}`}
      className="absolute -translate-x-1/2 -translate-y-1/2 focus:outline-none"
      style={{ left: `${x}%`, top: `${y}%` }}
    >
      {(st === "VACANT" || st === "LOCK") && (
        <span
          className="ss-ring absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full"
          style={{ width: 44, height: 44, border: `2px solid ${s.dot}` }}
        />
      )}
      <span
        className={`relative flex h-9 w-9 items-center justify-center rounded-full border-2 ${s.ring} ${
          isSel ? "bg-white/10 scale-110" : "bg-[#151515]"
        } transition-transform`}
        style={{ boxShadow: st !== "IDLE" ? `0 0 16px ${s.dot}80` : "none" }}
      >
        <MapPin size={16} style={{ color: s.dot }} strokeWidth={2.5} />
      </span>
      <span className={`mt-1 block whitespace-nowrap font-mono text-[9px] font-medium ${s.text}`}>
        {s.label}
      </span>
    </button>
  );
};

const MapCanvas = ({ providers, selectedId, activeSlot, bookedProviderId, onSelect }) => (
  <div
    className="relative w-full overflow-hidden rounded-xl border border-white/10 bg-[#0e0e0e]"
    style={MAP_BG}
    data-testid="map-canvas"
  >
    <div className="absolute left-3 top-3 font-mono text-[10px] text-zinc-600">45.478°N · 9.124°E</div>
    <svg className="absolute inset-0 h-full w-full pointer-events-none" aria-hidden>
      <line x1="0" y1="55%" x2="100%" y2="42%" stroke="rgba(255,255,255,0.06)" strokeWidth="10" />
      <line x1="20%" y1="0" x2="35%" y2="100%" stroke="rgba(255,255,255,0.05)" strokeWidth="8" />
    </svg>
    {providers.map((p) => (
      <MapPinMarker
        key={p.provider_id}
        provider={p}
        st={statusOf(p, activeSlot, bookedProviderId)}
        isSel={p.provider_id === selectedId}
        onSelect={onSelect}
      />
    ))}
  </div>
);

const ProviderRow = ({ provider, st, isSel, onSelect }) => {
  const s = STATUS_STYLES[st];
  return (
    <button
      onClick={() => onSelect(provider.provider_id)}
      data-testid={`provider-card-${provider.provider_id}`}
      className={`flex items-center gap-3 rounded-xl border px-3 py-2.5 text-left transition-all ${
        isSel ? "border-[#FF3B30]/60 bg-[#FF3B30]/10" : "border-white/10 bg-[#161616] hover:border-white/20"
      }`}
    >
      <img src={provider.image} alt={provider.business_name} className="h-11 w-11 rounded-lg object-cover" />
      <div className="min-w-0 flex-1">
        <p className="truncate font-body text-sm font-bold text-white">{provider.business_name}</p>
        <p className="truncate font-mono text-[11px] text-zinc-500">
          {catLabel(provider.category)} · {provider.demo_court.court_label}
        </p>
      </div>
      <span className={`font-mono text-[10px] font-bold ${s.text}`}>{s.label}</span>
    </button>
  );
};

const ProviderList = ({ providers, selectedId, activeSlot, bookedProviderId, onSelect }) => (
  <div className="mt-4 grid gap-2" data-testid="provider-list">
    {providers.map((p) => (
      <ProviderRow
        key={p.provider_id}
        provider={p}
        st={statusOf(p, activeSlot, bookedProviderId)}
        isSel={p.provider_id === selectedId}
        onSelect={onSelect}
      />
    ))}
  </div>
);

const SimulateButton = ({ selected, onSimulate, isTriggering, disabled }) => (
  <>
    <button
      data-testid="simulate-cancellation-button"
      onClick={() => selected && onSimulate(selected.provider_id)}
      disabled={disabled}
      className={`mt-5 flex w-full items-center justify-center gap-3 rounded-2xl bg-[#FF3B30] px-6 py-5 font-head text-xl sm:text-2xl font-extrabold uppercase tracking-wide text-white transition-transform ${
        disabled ? "opacity-40 cursor-not-allowed" : "ss-pulse hover:scale-[1.02] active:scale-[0.99]"
      }`}
    >
      <Zap size={26} strokeWidth={2.5} className={isTriggering ? "animate-spin" : ""} />
      {isTriggering ? "Sending Webhook…" : "Simulate Last-Minute Cancellation"}
    </button>
    <p className="mt-2 text-center font-mono text-[11px] text-zinc-500">
      Send webhook <span className="text-[#FFCC00]">CANCELLATION</span> →{" "}
      {selected ? selected.business_name : "—"}
    </p>
  </>
);

export const AdminMap = ({
  providers = [],
  selectedId,
  onSelect,
  activeSlot,
  bookedProviderId,
  onSimulate,
  isTriggering,
  disabled,
}) => {
  const selected = providers.find((p) => p.provider_id === selectedId);
  return (
    <div className="rounded-2xl border border-white/10 bg-[#121212] p-5 sm:p-6" data-testid="admin-panel">
      <PanelHeader />
      <MapCanvas
        providers={providers}
        selectedId={selectedId}
        activeSlot={activeSlot}
        bookedProviderId={bookedProviderId}
        onSelect={onSelect}
      />
      <ProviderList
        providers={providers}
        selectedId={selectedId}
        activeSlot={activeSlot}
        bookedProviderId={bookedProviderId}
        onSelect={onSelect}
      />
      <SimulateButton
        selected={selected}
        onSimulate={onSimulate}
        isTriggering={isTriggering}
        disabled={disabled}
      />
    </div>
  );
};

export default AdminMap;
